import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarX, House, RotateCw, UserRound } from 'lucide-react'
import { KioskStep } from '../../../../../components/kiosk-step'
import {
  KioskStatePanel,
  type KioskStateAction,
  type KioskStateTone,
} from '../../../../../components/kiosk-state-panel'
import { useSchedulerExitToStart, useSchedulerSelector, useSchedulerSend } from '../context'
import { useAudio } from '../../../../../lib/use-audio'

/** Distinguish an uncertain write outcome from a definite refused booking.
 * Every outcome is one state panel under the "Termin buchen" title (KO-11,
 * KO-12), with retry as ↻ and home as ⌂ at the same size (KO-17, SPK-03); the
 * uncertain outcomes tell the patient plainly to ask at the reception (KO-68).
 * @since 1.1.8
 * @example <SchedulerErrorStep />
 */
export function SchedulerErrorStep() {
  const outcome = useSchedulerSelector((snapshot) => snapshot.context.bookingFailure)
  if (outcome === 'rejected') return <RejectedBookingOutcome />
  if (outcome === 'assistance' || outcome === 'expired') return <AssistedBookingOutcome />
  return <UncertainBookingOutcome />
}

function RejectedBookingOutcome() {
  const { t } = useTranslation()
  useAudio('scheduler-error')
  return (
    <BookingOutcomeView
      tone="error"
      icon={<CalendarX />}
      title={t('self-service.scheduler.error.title')}
      body={`${t('self-service.scheduler.error.subtitle')} ${t('self-service.scheduler.error.info')}`}
      retry
    />
  )
}

function UncertainBookingOutcome() {
  const { t } = useTranslation()
  return (
    <BookingOutcomeView
      tone="warning"
      icon={<UserRound />}
      title={t('self-service.scheduler.error.pending-title')}
      body={t('self-service.scheduler.error.pending-info')}
      retry
    />
  )
}

function AssistedBookingOutcome() {
  const { t } = useTranslation()
  return (
    <BookingOutcomeView
      tone="warning"
      icon={<UserRound />}
      title={t('self-service.scheduler.error.pending-title')}
      body={t('self-service.scheduler.error.assistance-info')}
      retry={false}
    />
  )
}

function BookingOutcomeView({
  tone,
  icon,
  title,
  body,
  retry,
}: {
  readonly tone: KioskStateTone
  readonly icon: ReactNode
  readonly title: string
  readonly body: string
  readonly retry: boolean
}) {
  const { t } = useTranslation()
  const send = useSchedulerSend()
  const exitToStart = useSchedulerExitToStart()
  const home: KioskStateAction = {
    label: t('self-service.scheduler.done.back-to-start'),
    icon: House,
    onClick: exitToStart,
    testId: 'scheduler-error-home',
  }
  const retryAction: KioskStateAction = {
    label: t('common.retry'),
    icon: RotateCw,
    onClick: () => send({ type: 'BACK' }),
    testId: 'scheduler-error-retry',
  }
  return (
    <KioskStep width="narrow" align="center" title={t('self-service.scheduler.start.title')}>
      <KioskStatePanel
        tone={tone}
        icon={icon}
        testId="scheduler-error-display"
        title={title}
        body={body}
        action={retry ? retryAction : home}
        secondaryAction={retry ? home : undefined}
      />
    </KioskStep>
  )
}
